

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  MenuItem,
  Autocomplete,
  Paper,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionIcon from "@mui/icons-material/Description";
import dayjs from "dayjs";
import CancelIcon from "@mui/icons-material/Cancel";

// Services
import deliveryentryService from "../../../services/deliveryentry.Service";
import driverMasterService from "../../../services/masters/driverMaster.Service";
import productMasterService from "../../../services/masters/productMaster.Service";
import cylinderMasterService from "../../../services/masters/cylinderMaster.Service";
import vehicleMasterService from "../../../services/masters/vehicleMaster.Service";
import customerMasterService from "../../../services/masters/customerMaster.Service";
import uomMasterService from "../../../services/masters/uomMaster.Service";
import deliveryacknowledgement from "../../../services/deliveracknowledgment.service";

// ── CSS Styles ─────────────────────────────────────────────────────────────

const pageSx = {
  background: "#F4F7FB",
  minHeight: "100vh",
  padding: "20px 24px 40px",
  fontFamily: "'Inter', Arial, sans-serif",
};

const breadcrumbSx = {
  fontSize: "12px",
  color: "#718096",
  mb: 2,
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const pageHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mb: 2.5,
};

const pageTitleSx = {
  fontSize: "16px",
  fontWeight: 700,
  color: "#000000",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const backBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#2B6CB0",
  padding: "7px 14px",
  border: "1.5px solid #2B6CB0",
  borderRadius: "7px",
  background: "#fff",
  cursor: "pointer",
  textTransform: "none",
  "&:hover": { background: "#E6F1FB" },
};

const formCardSx = {
  background: "#fff",
  borderRadius: "8px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  border: "1px solid #E2E8F0",
  overflow: "hidden",
  mb: 2,
};

const sectionHeaderSx = {
  background: "#1C3A5E",
  padding: "12px 22px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const sectionHeaderTitleSx = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#fff",
  letterSpacing: "0.3px",
};

const formBodySx = { padding: "20px 22px" };

const fieldLabelSx = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#2063b6",
  mb: "4px",
  display: "flex",
  alignItems: "center",
  gap: "4px",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    background: "#fafafa",
    height: "36px",
    "& fieldset": { borderColor: "#E2E8F0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#CBD5E0" },
    "&.Mui-focused fieldset": { borderColor: "#2B6CB0", borderWidth: "2px" },
    "&.Mui-focused": { background: "#fff" },
    "&.Mui-disabled": { background: "#F8FAFC" },
  },
  "& input": {
    color: "#1A202C",
    fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    "&::placeholder": { color: "#A0AEC0", opacity: 1 },
  },
  "& .MuiInputLabel-root": { display: "none" },
  "& .MuiSelect-select": {
    height: "36px",
    display: "flex",
    alignItems: "center",
  },
};

const readonlyInputSx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    background: "#F0F4F8",
  },
};

const menuItemSx = { fontSize: "13px", fontFamily: "'Inter', Arial, sans-serif" };

const formFooterSx = {
  padding: "18px 24px",
  background: "#F8FAFC",
  borderTop: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "10px",
};

const saveBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "40px",
  background: "#4CAF50",
  color: "#fff",
  border: "none",
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none",
  fontFamily: "'Inter', Arial, sans-serif",
  "&:hover": { background: "#028b19", transform: "translateY(-1px)" },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": { background: "#94a3b8", color: "#fff" },
  transition: "all 0.15s ease",
  borderRadius: "8px",
};

const cancelBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "40px",
  background: "#E53E3E",
  color: "#ffffff",
  border: "none",
  "&:hover": { background: "#971f16", transform: "translateY(-1px)" },
  fontSize: "14px",
  fontWeight: 600,
  borderRadius: "8px",
  textTransform: "none",
};

// ─── TABLE STYLES ──────────────────────────────────────────────────────────
const fixedRowHeight = 45;

const tableContainerSx = {
  mt: 0,
  borderRadius: 0,
  border: "none",
  maxHeight: 400,
  overflowY: "auto",
};

const tableHeaderCellSx = {
  backgroundColor: "#1C3A5E",
  color: "#fff",
  fontSize: "12px",
  fontWeight: 600,
  padding: "0 12px",
  height: `${fixedRowHeight}px`,
  textAlign: "left",
  display: "table-cell",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
  borderRight: "1px solid rgba(255,255,255,0.1)",
  "&:first-of-type": { textAlign: "center" },
  "&:last-of-type": { textAlign: "right", borderRight: "none" },
};

const tableBodyCellSx = {
  padding: "0 8px",
  borderBottom: "1px solid #E2E8F0",
  height: `${fixedRowHeight}px`,
  verticalAlign: "middle",
  "&:first-of-type": { textAlign: "center" },
  "&:last-of-type": { textAlign: "right" },
};

const tableInputSx = {
  margin: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "transparent",
  "& .MuiOutlinedInput-root": {
    height: "100%",
    borderRadius: 0,
    backgroundColor: "transparent",
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
    alignItems: "center",
  },
  "& .MuiSelect-select": {
    height: "100%",
    display: "flex",
    alignItems: "center",
    padding: "0 12px",
    fontSize: "13px",
  },
  "& input": {
    padding: "0 12px",
    height: "100%",
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
  },
  "& .MuiInputBase-input": {
    height: "100%",
    boxSizing: "border-box",
  },
};

// ─────────────────────────────────────────────────────────────────────────────

const AddDeliveryEntry = ({ to_customer_id, from_vehicle_id, challan_no, driver_id }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { handleSubmit } = useForm();
  const location = useLocation();
  const editData = location.state || null;
  const login_details = sessionStorage.getItem("loginUser");
  const userD = JSON.parse(login_details);
  const userInfo = userD.result.userInfo;
  const depot_id = userInfo.depot_id;
const short_code = userInfo.depot_short_code || userInfo.plant_short_code;

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [showUnregisteredDriver, setShowUnregisteredDriver] = useState(!!editData?.unregistered_driver);
  const [showUnregisteredVehicle, setShowUnregisteredVehicle] = useState(!!editData?.unregistered_vehicle);
  const [challanList, setChallanList] = useState([]);
  const [selectedChallan, setSelectedChallan] = useState(null);
  const [loadingChallans, setLoadingChallans] = useState(false);

  const [data, setData] = useState({
    entry_id: editData ? editData.entry_id : undefined,
    entry_no: editData?.entry_no || challan_no || "",
    challanNo: editData?.challan_no || challan_no || "",
    entry_date: editData?.entry_date ? new Date(editData.entry_date).toISOString().split("T")[0] : "",
    driver_name: editData?.driver_name || "",
    vehicle_no: editData?.vehicle_no || "", 
    driver_id: editData?.driver_id || driver_id || "",
    from_tempo: editData?.from_tempo || from_vehicle_id || "",
    vehicle_id: editData?.vehicle_id || from_vehicle_id || "",
    to_customer: editData?.to_customer || to_customer_id || "",
    fin_year: editData?.fin_year || "",
    unregistered_driver: editData?.unregistered_driver || "",
    unregistered_vehicle: editData?.unregistered_vehicle || "",
  });

  const [driverList, setDriver] = useState([]);
  const [productList, setProduct] = useState([]);
  const [vehicleList, setVehicle] = useState([]);
  const [cylinderList, setCylinder] = useState([]);
  const [customerList, setCustomer] = useState([]);
  const [uomList, setUom] = useState([]);
  const [delivery_entry, setDelivery_entry] = useState([]);
console.log('depot_id being sent:', depot_id); 
useEffect(() => {
  async function fetchData() {
    try {
      const [driverData, productData, cylinderData, vehicleData, uomData, customerData, delivery_entry_data] =
        await Promise.all([
          driverMasterService.getDriverList({ page: 0, size: 999, searchKey: "" }),
          productMasterService.getProductList({ page: 0, size: 999, searchKey: "" }),
          cylinderMasterService.getCylinderList({ page: 0, size: 999, searchKey: "" }),
          vehicleMasterService.getVehicleList({ page: 0, size: 999, searchKey: "" }),
          uomMasterService.getUOMList({ page: 0, size: 999, searchKey: "" }),
         customerMasterService.getCustomerList({ page: 0, size: 999, searchKey: "", depot_id }),
          deliveryentryService.getDeliveryEntryList({ page: 0, size: 999, searchKey: "" }),
        ]);

        // Existing Promise.all ke baad ye add karo


      setDriver(driverData);
      setProduct(productData);
      setCylinder(cylinderData);
      setVehicle(vehicleData);
      setUom(uomData);
      setCustomer(customerData);
      setDelivery_entry(delivery_entry_data);

      if (driver_id) {
        const selectedDriver = driverData.find((d) => d.driver_id === driver_id);
        if (selectedDriver) setData((prev) => ({ ...prev, driver_name: selectedDriver.driver_name }));
      }

      // ✅ Entry No Generation — YAHAN ADD KARO
      if (!editData) {
        const lastEntryNo = delivery_entry_data?.[0]?.entry_no ?? "";
        console.log("Full delivery_entry_data:", delivery_entry_data);
  console.log("First item entry_no:", delivery_entry_data?.[0]?.entry_no);
  console.log("All entry_nos:", delivery_entry_data?.map(d => d.entry_no));

        const parts = String(lastEntryNo).split("/");
        const lastSeq = parseInt(parts[parts.length - 1]) || 0;
        const nextSeq = lastSeq + 1;

        const yr = dayjs();
        const finYear = yr.month() >= 3
          ? `${String(yr.year()).slice(-2)}-${String(yr.year() + 1).slice(-2)}`
          : `${String(yr.year() - 1).slice(-2)}-${String(yr.year()).slice(-2)}`;

     const formattedEntryNo = `DL/${finYear}/${String(nextSeq).padStart(4, "0")}`;
        console.log("Generated entry_no:", formattedEntryNo);

        setData(prev => ({
          ...prev,
          entry_no: formattedEntryNo,
          fin_year: finYear,
        }));
      }

    } catch (error) {
      console.error("Data fetching failed", error);
      setErrorMessage("Error fetching initial data");
    }
  }
  fetchData();
}, [driver_id]);

  const [table_details, settable_details] = useState(
    editData?.table_details
      ? JSON.parse(editData.table_details).map((item, index) => ({
          srno: index + 1,
          cyl_id: item.cyl_id || "",
          cyl_no: item.cyl_no || "",
          prod_id: item.prod_id || "",
          prod_code: item.prod_code || item.product_code || "",
          prod_description: item.product_description || "",
          // qtykg: item.qty || item.bill_qty || "",
          qtykg: item.bill_qty || item.qty || "",
          uom: item.uom || "",
          customer_name: editData.customer_name || "",
        }))
      : [{ srno: "", cyl_no: "", prod_code: "", prod_description: "", qtykg: "", uom: "", customer_name: "" }]
  );

  

  useEffect(() => {
  if (selectedChallan && selectedChallan.table_details) {
    console.log("=== selectedChallan ===", selectedChallan);
    console.log("driver_id:", selectedChallan.driver_id);
    console.log("driver_name:", selectedChallan.driver_name);
    console.log("vehicle_id:", selectedChallan.vehicle_id);
    console.log("vehicle_no:", selectedChallan.vehicle_no);
    console.log("from_vehicle_id:", selectedChallan.from_vehicle_id);
    try {
      const tableDetails = JSON.parse(selectedChallan.table_details);
      const formattedTableDetails = tableDetails.map((item, index) => {
        const customer = customerList.find((c) => c.cust_id === selectedChallan.to_customer_id);
        const selectedProduct = productList.find((p) => p.prod_code === (item.prod_code || item.product_code));
        const selectedCylinder = cylinderList.find((c) => c.cylinder_no === item.cyl_no);
        return {
          srno: index + 1,
          cyl_id: selectedCylinder?.cylinder_id || item.cyl_id || "",
          cyl_no: item.cyl_no || "",
          prod_id: selectedProduct?.prod_id || item.prod_id || "",
          prod_code: item.prod_code || item.product_code || "",
          prod_description: selectedProduct?.prod_description || item.product_description || "",
          // qtykg: item.qty || item.bill_qty || "",
          qtykg: item.bill_qty || item.qty || "",
          uom: item.uom || "",
          customer_name: customer?.cust_name || selectedChallan.customer_name || "",
        };
      });
      settable_details(formattedTableDetails);

      // ✅ Driver, Vehicle, Customer auto-fill
      const challanDriver = driverList.find(d => d.driver_id === selectedChallan.driver_id);
      
      const challanVehicle = vehicleList.find(v => 
  v.vehicle_id === selectedChallan.vehicle_id || 
  v.vehicle_id === selectedChallan.from_vehicle_id
);

setData((prev) => ({
  ...prev,
 driver_id: selectedChallan.driver_id || prev.driver_id,
  driver_name: selectedChallan.driver_name || challanDriver?.driver_name || prev.driver_name,
  vehicle_no: selectedChallan.vehicle_no || challanVehicle?.vehicle_no || prev.vehicle_no,  // ← yeh directly aa raha hai
  from_tempo: selectedChallan.from_vehicle_id || selectedChallan.vehicle_id || prev.from_tempo,
  vehicle_id: selectedChallan.from_vehicle_id || selectedChallan.vehicle_id || prev.vehicle_id,
  to_customer: selectedChallan.to_customer_id || to_customer_id || prev.to_customer,
  entry_no: selectedChallan.entry_no || challan_no || prev.entry_no,
  challan_no: selectedChallan.entry_no || challan_no || prev.challan_no,
  entry_date: selectedChallan.entry_date || prev.entry_date,
}));

    } catch (error) {
      console.error("Error parsing table_details:", error);
      setErrorMessage("Error parsing challan table details");
    }
  } else {
    settable_details([{ srno: "", cyl_no: "", prod_code: "", prod_description: "", qtykg: "", uom: "", customer_name: "" }]);
  }
}, [selectedChallan, to_customer_id, challan_no, customerList, productList, cylinderList, driverList, vehicleList, from_vehicle_id]);

  const toggleUnregisteredDriver = () => {
    setShowUnregisteredDriver((prev) => !prev);
    if (showUnregisteredDriver) setData((prev) => ({ ...prev, unregistered_driver: "" }));
  };

  const toggleUnregisteredVehicle = () => {
    setShowUnregisteredVehicle((prev) => !prev);
    if (showUnregisteredVehicle) setData((prev) => ({ ...prev, unregistered_vehicle: "" }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => {
      const updatedData = { ...prev, [name]: value };
      if (name === "entry_date") {
        const selectedDate = new Date(value);
        const month = selectedDate.getMonth();
        const year = selectedDate.getFullYear();
        updatedData.fin_year = month >= 3 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
      } else if (name === "driver_name") {
        const selectedDriver = driverList.find((d) => d.driver_name === value);
        updatedData.driver_id = selectedDriver ? selectedDriver.driver_id : "";
        if (value) { updatedData.unregistered_driver = ""; setShowUnregisteredDriver(false); }
      } else if (name === "from_tempo") {
        const selectedVehicle = vehicleList.find((v) => v.vehicle_id === value);
        updatedData.vehicle_id = selectedVehicle ? selectedVehicle.vehicle_id : "";
        if (value) { updatedData.unregistered_vehicle = ""; setShowUnregisteredVehicle(false); }
      } else if (name === "unregistered_driver" && value) {
        updatedData.driver_name = ""; updatedData.driver_id = "";
      } else if (name === "unregistered_vehicle" && value) {
        updatedData.from_tempo = ""; updatedData.vehicle_id = "";
      } else if (name === "to_customer") {
  const selectedCustomer = customerList.find((c) => c.cust_id === value);
  updatedData.to_customer = selectedCustomer ? selectedCustomer.cust_id : "";
  settable_details((prev) => prev.map((row) => ({ ...row, customer_name: selectedCustomer?.cust_name || "" })));
  setChallanList([]);
  setSelectedChallan(null);
}
      return updatedData;
    });
  };

  const handleChangeInput = (e, index) => {
    const { name, value } = e.target;
    const updated = [...table_details];
    updated[index][name] = value;
    if (name === "prod_code") {
      const selectedProduct = productList.find((prod) => prod.prod_code === value);
      updated[index]["prod_description"] = selectedProduct?.prod_description || "";
      updated[index]["prod_id"] = selectedProduct?.prod_id || "";
    } else if (name === "cyl_no") {
      const selectedCylinder = cylinderList.find((cyl) => cyl.cylinder_no === value);
      updated[index]["cyl_id"] = selectedCylinder?.cylinder_id || "";
    }
    settable_details(updated);
  };

  const handleAddSPQuotationFields = () => {
    settable_details((prev) => [
      ...prev,
      {
        srno: "", cyl_no: "", prod_code: "", prod_description: "", qtykg: "", uom: "",
        customer_name: data.to_customer ? customerList.find((c) => c.cust_id === data.to_customer)?.cust_name || "" : "",
      },
    ]);
  };

  const handleRemovepickup = (index) => {
    settable_details((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async () => {
     console.log("SUBMIT data:", data);
  console.log("SUBMIT vehicle_no:", data.vehicle_no);
  console.log("SUBMIT driver_name:", data.driver_name);
    let validationErrors = [];

if (!data.vehicle_no && !data.from_tempo && !data.unregistered_vehicle) validationErrors.push("Error Occurred! Vehicle is required");
    if (!data.to_customer) validationErrors.push("Error Occurred! Customer is required");
    if (!table_details || table_details.length === 0) {
      validationErrors.push("Error Occurred! At least one cylinder must be selected");
    } else {
   const invalidRows = table_details.filter((row) => !row.cyl_no || !row.qtykg || !row.uom);
      if (invalidRows.length > 0) validationErrors.push("Error Occurred! All table fields are required");
    }
    if (validationErrors.length > 0) { setErrorMessage(validationErrors.join(", ")); setSuccessMessage(""); return; }

    const isEdit = Boolean(editData);
   const formattedTableDetails = table_details.map((item, index) => {
      const enteredKg = parseFloat(item.qtykg) || 0;
      const isCum = (item.uom || "").trim().toUpperCase() === "CUM";
      const convertedQty = isCum ? parseFloat((enteredKg * 0.96).toFixed(2)) : enteredKg;
      return {
        sr_no: index + 1, cyl_id: item.cyl_id || "", cyl_no: item.cyl_no, prod_id: item.prod_id || "",
        prod_code: item.prod_code, prod_description: item.prod_description,
        qtykg: enteredKg, qty: convertedQty, bill_qty: convertedQty, uom: item.uom,
      };
    });

    const deliveryEntryPayload = {
      entry_id: isEdit ? data.entry_id : null,
      entry_no: data.entry_no,
      entry_date: data.entry_date ? dayjs(data.entry_date).format("YYYY-MM-DD") : "",
       vehicle_no: data.vehicle_no,   
      driver_name: data.driver_name, driver_id: data.driver_id,
      from_tempo: data.from_tempo, from_vehicle_id: data.vehicle_id,
      to_customer_id: data.to_customer,
      unregistered_driver: data.unregistered_driver,
      unregistered_vehicle: data.unregistered_vehicle,
      table_details: JSON.stringify(formattedTableDetails),
      modified_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      modified_by: editData ? userInfo.user_id : null,
      created_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      created_by: editData ? editData.created_by : userInfo.user_id,
      changedUpdatedValue: isEdit ? "edit" : "add",
      depot_id, short_code, challanNo: data.challan_no || data.entry_no,
    };

    const formData = new FormData();
    formData.append("deliveryData", JSON.stringify(deliveryEntryPayload));
    if (receiptFile) formData.append("upload_receipt", receiptFile);

    try {
      if (isEdit) await deliveryentryService.editDeleteDeliveryEntry(formData);
      else await deliveryentryService.addDeliveryEntry(formData);
      setSuccessMessage(isEdit ? "Updated successfully" : "Added successfully");
      setErrorMessage("");
      setTimeout(() => navigate(-1), 1000);
    } catch (error) {
      console.error("Submission failed:", error);
      setErrorMessage("Error occurred during submission");
      setSuccessMessage("");
    }
  };

//  useEffect(() => {
//   if (data.to_customer) {
//     setLoadingChallans(true);
//     deliveryacknowledgement
//       .getDeliveryChallanList(data.to_customer)
//         .then((response) => {
//           if (Array.isArray(response)) {
//             setChallanList(response);
//             if (challan_no) {
//               const selected = response.find((c) => c.entry_no === challan_no);
//               setSelectedChallan(selected || null);
//               if (!selected) setErrorMessage(`Error: No challan found for challan_no ${challan_no}`);
//             }
//           } else {
//             setErrorMessage("Error: Invalid challan list response");
//           }
//           setLoadingChallans(false);
//         })
//         .catch(() => { setErrorMessage("Error fetching challans"); setLoadingChallans(false); });
//     } else {
//       setChallanList([]); setSelectedChallan(null);
//       settable_details([{ srno: "", cyl_no: "", prod_code: "", prod_description: "", qtykg: "", uom: "", customer_name: "" }]);
//     }
//   }, [data.driver_id, challan_no]);

//   useEffect(() => {
//   if (!data.to_customer) {
//     setChallanList([]);
//     setSelectedChallan(null);
//     settable_details([{ srno: "", cyl_no: "", prod_code: "", prod_description: "", qtykg: "", uom: "", customer_name: "" }]);
//     return;
//   }

//   setLoadingChallans(true);
//   deliveryacknowledgement
//     .getDeliveryChallanList(data.to_customer)
//     .then((response) => {
//       console.log("Challan API response:", response); // dekho kya aa raha hai
//       // response array hai ya { data: [...] } ?
//       const list = Array.isArray(response) ? response : response?.data || [];
//       setChallanList(list);
//       setLoadingChallans(false);
//     })
//     .catch((err) => {
//       console.error("Challan fetch error:", err);
//       setErrorMessage("Error fetching challans");
//       setLoadingChallans(false);
//     });
// }, [data.to_customer]);

useEffect(() => {
  setLoadingChallans(true);

  // ✅ logged-in user ka depot_id session se nikalo
  const loginDetails = sessionStorage.getItem("loginUser");
  const userInfo = loginDetails ? JSON.parse(loginDetails)?.result?.userInfo : null;
  const depotId = userInfo?.depot_id || userInfo?.assigned_depot_id || null;

  deliveryacknowledgement
    .getAllChallanList(depotId)
    .then((response) => {
      console.log("Challan API response:", response);
      const list = Array.isArray(response) ? response : response?.data || [];
      setChallanList(list);
      setLoadingChallans(false);
    })
    .catch((err) => {
      console.error("Challan fetch error:", err);
      setErrorMessage("Error fetching challans");
      setLoadingChallans(false);
    });
}, []);

const handleBack = () => navigate(-1);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={pageSx}>
        {/* Breadcrumb */}
        <Box sx={breadcrumbSx}>
          <span>Dashboard</span> / <span style={{ color: "#2B6CB0", fontWeight: 600 }}>Delivery Entry</span>
        </Box>

        {/* Page Header */}
        <Box sx={pageHeaderSx}>
          <Box sx={pageTitleSx}>
            <DescriptionIcon sx={{ fontSize: 20, color: "#1C3A5E" }} />
            {editData ? "UPDATE" : "ADD"} DELIVERY ACKNOWLEDGEMENT
          </Box>
          <Button onClick={handleBack} sx={backBtnSx}>
            <ArrowBackIcon sx={{ fontSize: 16 }} /> Back
          </Button>
        </Box>

        {/* Last Entry Info */}
        {!editData && delivery_entry && (
          <Box sx={{ fontSize: "12px", color: "#718096", textAlign: "right", mb: 1.5 }}>
            Last Entry No : {delivery_entry.length > 0 ? delivery_entry[0].lastInternalEntryNo : "0"}
          </Box>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* ── Main Details Card ─────────────────────────────────── */}
          <Box sx={formCardSx}>
            <Box sx={sectionHeaderSx}>
              <DescriptionIcon sx={{ fontSize: 18, color: "#fff" }} />
              <Typography sx={sectionHeaderTitleSx}>Delivery Acknowledgement Details</Typography>
            </Box>

            <Box sx={formBodySx}>
              <Grid container sx={{ border: "1px solid #E2E8F0", borderRadius: "10px", overflow: "hidden" }}>

                {/* ════════ LEFT COLUMN ════════ */}
                <Grid
                  item xs={12} md={6}
                  sx={{
                    p: 2.5,
                  
                    borderBottom: { xs: "2px dashed #CBD5E0", md: "none" },
                  }}
                >

                  
                  <Grid container spacing={1.5}>


                    {/* <Grid item xs={12} sm={5}>
                      <Typography sx={fieldLabelSx}>Customer</Typography>
                      <Autocomplete
                        disablePortal
                        options={customerList || []}
                        clearIcon={false}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.cust_name || ""
                        }
                        value={
                          customerList.find((c) => c.cust_id === data.to_customer) ||
                          (data.to_customer ? { cust_id: data.to_customer } : null)
                        }
                        onChange={(event, newValue) => {
                          handleChange({
                            target: { name: "to_customer", value: newValue ? newValue.cust_id : "" },
                          });
                        }}
                        isOptionEqualToValue={(option, value) => option.cust_id === value.cust_id}
                        renderOption={(props, option) => (
                          <MenuItem {...props} sx={menuItemSx}>{option.cust_name}</MenuItem>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            fullWidth
                            InputProps={{ ...params.InputProps, sx: { height: "36px" } }}
                            sx={inputSx}
                          />
                        )}
                        sx={{ width: "100%" }}
                      />
                    </Grid> */}


 <Grid item xs={12} sm={5}>
                      <Typography sx={fieldLabelSx}>Challan No</Typography>
                      <Autocomplete
                        disablePortal
                        options={challanList || []}
                        clearIcon={false}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.entry_no || ""
                        }
                        value={selectedChallan}
                        onChange={(event, newValue) => setSelectedChallan(newValue)}
                        isOptionEqualToValue={(option, value) => option.entry_no === value.entry_no}
                        renderOption={(props, option) => (
                          <MenuItem {...props} sx={menuItemSx}>{option.entry_no}</MenuItem>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            fullWidth
                            InputProps={{ ...params.InputProps, sx: { height: "36px" } }}
                            sx={inputSx}
                          />
                        )}
                        sx={{ width: "100%" }}
                        // disabled={loadingChallans || !data.driver_id}
                     disabled={loadingChallans}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
  <Typography sx={fieldLabelSx}>Customer</Typography>
  <TextField
    size="small"
    fullWidth
    value={
      customerList.find((c) => c.cust_id === data.to_customer)?.cust_name || ""
    }
    sx={readonlyInputSx}
    InputProps={{ readOnly: true }}
    placeholder="Auto-filled from challan"
  />
</Grid>
                   

                      {/* <Grid item xs={12} sm={5}>
                      <Typography sx={fieldLabelSx}>Customer</Typography>
                      <Autocomplete
                        disablePortal
                        options={customerList || []}
                        clearIcon={false}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.cust_name || ""
                        }
                        value={
                          customerList.find((c) => c.cust_id === data.to_customer) ||
                          (data.to_customer ? { cust_id: data.to_customer } : null)
                        }
                        onChange={(event, newValue) => {
                          handleChange({
                            target: { name: "to_customer", value: newValue ? newValue.cust_id : "" },
                          });
                        }}
                        isOptionEqualToValue={(option, value) => option.cust_id === value.cust_id}
                        renderOption={(props, option) => (
                          <MenuItem {...props} sx={menuItemSx}>{option.cust_name}</MenuItem>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            fullWidth
                            InputProps={{ ...params.InputProps, sx: { height: "36px" } }}
                            sx={inputSx}
                          />
                        )}
                        sx={{ width: "100%" }}
                      />
                    </Grid> */}

                    {/* ROW 1: Driver (wider) + Challan No (narrower) — same row */}
                    {/* <Grid item xs={12} sm={5}>
                      <Typography sx={fieldLabelSx}>Driver</Typography>
                      <Autocomplete
                         freeSolo
  disablePortal
  options={[]}
  open={false}
                        clearIcon={false}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.driver_name || ""
                        }
                        value={
                          driverList.find((d) => d.driver_id === data.driver_id) ||
                          (data.driver_id ? { driver_id: data.driver_id, driver_name: data.driver_name } : null)
                        }
                        onChange={(event, newValue) => {
                          setData((prev) => ({
                            ...prev,
                            driver_name: newValue ? newValue.driver_name : "",
                            driver_id: newValue ? newValue.driver_id : "",
                            unregistered_driver: newValue ? "" : prev.unregistered_driver,
                          }));
                          setShowUnregisteredDriver(!newValue);
                          setSelectedChallan(null);
                        }}
                        isOptionEqualToValue={(option, value) => option.driver_id === value.driver_id}
                        renderOption={(props, option) => (
                          <MenuItem {...props} sx={menuItemSx}>{option.driver_name}</MenuItem>
                        )}
                        renderInput={(params) => (
                           <TextField
    size="small"
    fullWidth
    value={data.driver_name || ""}
    sx={readonlyInputSx}
    InputProps={{ readOnly: true }}
    placeholder="Auto-filled from challan"
  />
                        )}
                        sx={{ width: "100%" }}
                      />
                    </Grid> */}

                    <Grid item xs={12} sm={5}>
  <Typography sx={fieldLabelSx}>Driver</Typography>
  <TextField
    size="small"
    fullWidth
    value={data.driver_name || ""}
    sx={readonlyInputSx}
    InputProps={{ readOnly: true }}
    placeholder="Auto-filled from challan"
  />
</Grid>

                    {/* Challan No — same row as Driver, sm=5 (narrower) */}
                    {/* <Grid item xs={12} sm={5}>
                      <Typography sx={fieldLabelSx}>Challan No</Typography>
                      <Autocomplete
                        disablePortal
                        options={challanList || []}
                        clearIcon={false}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.entry_no || ""
                        }
                        value={selectedChallan}
                        onChange={(event, newValue) => setSelectedChallan(newValue)}
                        isOptionEqualToValue={(option, value) => option.entry_no === value.entry_no}
                        renderOption={(props, option) => (
                          <MenuItem {...props} sx={menuItemSx}>{option.entry_no}</MenuItem>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            fullWidth
                            InputProps={{ ...params.InputProps, sx: { height: "36px" } }}
                            sx={inputSx}
                          />
                        )}
                        sx={{ width: "100%" }}
                        disabled={loadingChallans || !data.driver_id}
                      />
                    </Grid> */}

                    {/* ROW 2: Vehicle No */}
                    <Grid item xs={12} sm={5}>
                      <Typography sx={fieldLabelSx}>Vehicle No</Typography>
                      <Autocomplete
                         freeSolo
  disablePortal
  options={[]}
  open={false}
                        clearIcon={false}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.vehicle_no || ""
                        }
                        value={
                          vehicleList.find((v) => v.vehicle_id === data.from_tempo) ||
                          (data.from_tempo ? { vehicle_id: data.from_tempo } : null)
                        }
                        onChange={(event, newValue) => {
                          handleChange({
                            target: { name: "from_tempo", value: newValue ? newValue.vehicle_id : "" },
                          });
                        }}
                        isOptionEqualToValue={(option, value) => option.vehicle_id === value.vehicle_id}
                        renderOption={(props, option) => (
                          <MenuItem {...props} sx={menuItemSx}>{option.vehicle_no}</MenuItem>
                        )}
                        renderInput={(params) => (
                           <TextField
    size="small"
    fullWidth
    value={data.vehicle_no || ""}
    sx={readonlyInputSx}
    InputProps={{ readOnly: true }}
    placeholder="Auto-filled from challan"
  />
                        )}
                        sx={{ width: "100%" }}
                      />
                    </Grid>

                    {/* ROW 3: To Customer */}
                    {/* <Grid item xs={12} sm={5}>
                      <Typography sx={fieldLabelSx}>Customer</Typography>
                      <Autocomplete
                        disablePortal
                        options={customerList || []}
                        clearIcon={false}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.cust_name || ""
                        }
                        value={
                          customerList.find((c) => c.cust_id === data.to_customer) ||
                          (data.to_customer ? { cust_id: data.to_customer } : null)
                        }
                        onChange={(event, newValue) => {
                          handleChange({
                            target: { name: "to_customer", value: newValue ? newValue.cust_id : "" },
                          });
                        }}
                        isOptionEqualToValue={(option, value) => option.cust_id === value.cust_id}
                        renderOption={(props, option) => (
                          <MenuItem {...props} sx={menuItemSx}>{option.cust_name}</MenuItem>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            fullWidth
                            InputProps={{ ...params.InputProps, sx: { height: "36px" } }}
                            sx={inputSx}
                          />
                        )}
                        sx={{ width: "100%" }}
                      />
                    </Grid> */}

                    {/* ROW 4: Upload Receipt */}
                   <Grid item xs={12} sm={8}>
  <Typography sx={fieldLabelSx}>Upload Receipt</Typography>
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      border: "1.5px solid #E2E8F0",
      borderRadius: "8px",
      background: "#fafafa",
      height: "36px",
      overflow: "hidden",
      "&:hover": { borderColor: "#CBD5E0" },
    }}
  >
    <Button
      component="label"
      size="small"
      sx={{
        fontSize: "12px",
        textTransform: "none",
        background: "#E2E8F0",
        color: "#2D3748",
        fontWeight: 600,
        height: "100%",
        borderRadius: "6px 0 0 6px",
        px: 2,
        minWidth: "fit-content",
        whiteSpace: "nowrap",
        "&:hover": { background: "#CBD5E0" },
      }}
    >
      Choose File
      <input
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => setReceiptFile(e.target.files[0])}
      />
    </Button>
    <Typography
      sx={{
        fontSize: "12px",
        color: receiptFile ? "#1A202C" : "#A0AEC0",
        px: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        flex: 1,
         minWidth: "160px",
      }}
    >
      {receiptFile ? receiptFile.name : "No file chosen"}
    </Typography>
     {receiptFile && (
    <IconButton
      size="small"
      onClick={() => setReceiptFile(null)}
      sx={{ color: "#E53E3E", mr: 0.5, "&:hover": { background: "#FFF5F5" } }}
    >
      <CancelIcon sx={{ fontSize: "16px" }} />
    </IconButton>
  )}
  </Box>
</Grid>

                  </Grid>
                </Grid>

                {/* ════════ RIGHT COLUMN — Entry No & Entry Date side by side ════════ */}
                <Grid
                  item xs={12} md={6}
                  sx={{ p: 2.5, background: "#FAFBFC", pl: 3.5 }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0 }}>

                    {/* Entry No */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={fieldLabelSx}>Entry No</Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={data.entry_no}
                        variant="outlined"
                        sx={readonlyInputSx}
                        InputProps={{ readOnly: true }}
                        placeholder="Auto-generated"
                      />
                    </Box>

                    {/* Vertical separator */}
                    <Box
                      sx={{
                        // width: "1px",
                        // background: "#CBD5E0",
                        mx: 2,
                        mt: "22px",
                        // height: "36px",
                        // flexShrink: 0,
                      }}
                    />

                    {/* Entry Date */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={fieldLabelSx}>
                        Entry Date <span style={{ color: "#E53E3E" }}>*</span>
                      </Typography>
                      <DatePicker
                        format="DD/MM/YYYY"
                     value={data.entry_date ? dayjs(data.entry_date) : null}
                        onChange={(newValue) =>
                          handleChange({
                            target: { name: "entry_date", value: newValue ? newValue.toDate() : "" },
                          })
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            required: true,
                            sx: inputSx,
                          },
                        }}
                      />
                    </Box>

                  </Box>
                </Grid>

              </Grid>
            </Box>
          </Box>

          {/* ── Table Card ──────────────────────────────────────────── */}
          <Box sx={formCardSx}>
            {/* <Box sx={sectionHeaderSx}>
              <DescriptionIcon sx={{ fontSize: 18, color: "#fff" }} />
              <Typography sx={sectionHeaderTitleSx}>Cylinder Details</Typography>
            </Box> */}

            <TableContainer component={Paper} sx={tableContainerSx} elevation={0}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeaderCellSx} width="5%">Sr No</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Cylinder No</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Product Code</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Product Description</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Qty</TableCell>
                    <TableCell sx={tableHeaderCellSx}>UOM</TableCell>
                    <TableCell sx={tableHeaderCellSx} align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ backgroundColor: "#fff" }}>
                  {table_details &&
                    table_details.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          height: `${fixedRowHeight}px`,
                          background: index % 2 === 0 ? "#fff" : "#F8FAFC",
                          "&:hover": { background: "#EBF4FF" },
                        }}
                      >
                        <TableCell sx={tableBodyCellSx}>{index + 1}</TableCell>
                       <TableCell sx={tableBodyCellSx}>
  <TextField
    fullWidth
    name="cyl_no"
    value={row.cyl_no}
    sx={tableInputSx}
    InputProps={{ readOnly: true }}
    placeholder="Auto-filled"
  />
</TableCell>
                        <TableCell sx={tableBodyCellSx}>
  <TextField
    fullWidth
    name="prod_code"
    value={row.prod_code}
    sx={tableInputSx}
    InputProps={{ readOnly: true }}
    placeholder="Auto-filled"
  />
</TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <TextField
                            fullWidth name="prod_description" value={row.prod_description}
                            sx={tableInputSx} InputProps={{ readOnly: true }}
                          />
                        </TableCell>
                       <TableCell sx={tableBodyCellSx}>
  <TextField
    fullWidth placeholder="Auto-filled" type="text"
    name="qtykg" value={row.qtykg}
    sx={tableInputSx} InputProps={{ readOnly: true }}
  />
</TableCell>
                       <TableCell sx={tableBodyCellSx}>
  <TextField
    fullWidth
    name="uom"
    value={row.uom}
    sx={tableInputSx}
    InputProps={{ readOnly: true }}
    placeholder="Auto-filled"
  />
</TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "5px", height: "100%", alignItems: "center" }}>
                            <IconButton size="small" onClick={handleAddSPQuotationFields}>
                              <AddIcon sx={{ fontSize: "16px", color: "#2B6CB0", border: "1px solid #2B6CB0", borderRadius: "50%" }} />
                            </IconButton>
                            {index === 0 ? (
                              <IconButton disabled size="small">
                                <DeleteForeverIcon sx={{ fontSize: "18px", color: "#c0c0c0" }} />
                              </IconButton>
                            ) : (
                              <IconButton size="small" onClick={() => handleRemovepickup(index)}>
                                <DeleteForeverIcon sx={{ fontSize: "18px", color: "#E53E3E" }} />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Messages */}
            {(successMessage || errorMessage) && (
              <Box sx={{ px: 3, pt: 1.5 }}>
                {successMessage && (
                  <Typography sx={{ color: "#4CAF50", fontSize: "14px", fontWeight: 500 }}>{successMessage}</Typography>
                )}
                {errorMessage && (
                  <Typography sx={{ color: "#E53E3E", fontSize: "14px", fontWeight: 500 }}>{errorMessage}</Typography>
                )}
              </Box>
            )}

            {/* Form Footer */}
            <Box sx={formFooterSx}>
              <Button sx={cancelBtnSx} onClick={handleBack}>Cancel</Button>
              <Button type="submit" sx={saveBtnSx}>
                <SaveIcon sx={{ fontSize: 18 }} />
                {editData ? "Update" : "Save"}
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </LocalizationProvider>
  );
};

export default AddDeliveryEntry;